import { Location } from '@angular/common';
import { DestroyRef, Injectable, Injector, NgZone, Signal, inject } from '@angular/core';
import {
  Event as RouterEvent,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationExtras,
  NavigationSkipped,
  NavigationStart,
  Router,
  UrlTree,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavController, Platform } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, map, skip } from 'rxjs/operators';
import { AuthService, ParsedToken } from '@services/auth.service';
import { SessionStorageService } from '@services/session-storage.service';
import {
  isInternalStateRouteUrl,
  normalizeStateRoutePath,
  normalizeStateRouteUrl,
  STATE_ROUTE_ROOT,
} from '@state-routing/config/state-routing.config';
import { StateRouteBrowserEntry, StateRouteEntry } from '@state-routing/models/state-route.models';
import {
  PendingNativeTraversal,
  setStateHistoryGuard,
} from '@state-routing/services/state-history-guard';
import { StateOverlayService } from '@state-routing/services/state-overlay.service';
import { StateRouteStore } from '@state-routing/services/state-route.store';

interface PendingStateNavigation {
  readonly replaceUrl: boolean;
  readonly trigger: string;
}

interface PendingBrowserTraversal {
  readonly expectedUrl: string;
  readonly isExpectedState: () => boolean;
  readonly complete: (result: boolean) => void;
}

interface StateNavigationOptions extends NavigationExtras {
  animated?: boolean;
}

@Injectable()
export class StateRouterService {
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly location = inject(Location);
  private readonly navController = inject(NavController);
  private readonly ngZone = inject(NgZone);
  private readonly platform = inject(Platform);
  private readonly router = inject(Router);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly injector = inject(Injector);

  private static readonly BROWSER_STATE_KEY: string = 'stateRouteBrowserEntry';
  private static readonly LEGACY_BROWSER_STATE_SCHEMA_VERSION: number = 1;
  private static readonly BROWSER_STATE_SCHEMA_VERSION: number = 2;
  private static readonly HISTORY_TRAVERSAL_TIMEOUT_MS: number = 2_000;

  private readonly store = new StateRouteStore(this.sessionStorage);
  private readonly browserTraversalOperations = new Set<() => void>();
  private readonly rejectedForwardNavigationIds = new Set<number>();
  private readonly handleBrowserPopState = (event: PopStateEvent): boolean => {
    const overlayIntercepted: boolean = this.getStateOverlay().interceptBrowserPopState(event);
    if (overlayIntercepted) return true;

    const pendingNativeTraversal: PendingNativeTraversal | null = this.pendingNativeTraversal;
    if (pendingNativeTraversal) {
      this.ngZone.run((): void => {
        pendingNativeTraversal.complete(pendingNativeTraversal.isExpectedState());
      });
      return true;
    }

    const acceptedEntry: StateRouteBrowserEntry | null = this.currentBrowserEntry;
    if (!acceptedEntry) return false;

    const pendingBrowserTraversal: PendingBrowserTraversal | null = this.pendingBrowserTraversal;
    const expectedBackUrl: string = pendingBrowserTraversal?.expectedUrl
      ?? this.resolveBackTargetUrl(acceptedEntry.url, STATE_ROUTE_ROOT);
    const targetEntry: StateRouteBrowserEntry | null = this.readBrowserEntry(
      event.state,
      this.resolveCurrentUrl(),
    );
    if (!targetEntry) {
      this.ngZone.run((): void => {
        void this.recoverUnexpectedBrowserBack(expectedBackUrl, acceptedEntry).then((): void => {
          pendingBrowserTraversal?.complete(pendingBrowserTraversal.isExpectedState());
        });
      });
      return true;
    }

    if (targetEntry.position < acceptedEntry.position) {
      if (normalizeStateRouteUrl(targetEntry.url) !== expectedBackUrl) {
        this.ngZone.run((): void => {
          void this.recoverUnexpectedBrowserBack(
            expectedBackUrl,
            acceptedEntry,
            targetEntry.position,
          ).then((): void => {
            pendingBrowserTraversal?.complete(pendingBrowserTraversal.isExpectedState());
          });
        });
        return true;
      }
      return false;
    }
    if (targetEntry.position === acceptedEntry.position) return false;

    this.ngZone.run((): void => {
      void this.rejectForwardTraversal(acceptedEntry, false);
    });
    return true;
  };
  private authOwnerResetOperation: Promise<void> | null = null;
  private authOwnerRevision: number = 0;
  private backOperation: Promise<boolean> | null = null;
  private currentBrowserEntry: StateRouteBrowserEntry | null = null;
  private pendingBrowserTraversal: PendingBrowserTraversal | null = null;
  private pendingNativeTraversal: PendingNativeTraversal | null = null;
  private pendingNavigation: PendingStateNavigation | null = null;
  private sectionNavigationOperation: Promise<boolean> | null = null;
  private settledNavigationExists: boolean = false;
  private stateOverlay: StateOverlayService | null = null;
  private forwardRejectionOperation: Promise<void> | null = null;
  private programmaticBrowserTraversalCount: number = 0;
  private unexpectedBackOperation: Promise<void> | null = null;
  private started: boolean = false;
  private hardwareBackSubscription: Subscription | null = null;

  public get routeStack(): Signal<readonly StateRouteEntry[]> {
    return this.store.routes;
  }

  public start(): void {
    if (this.started) return;

    this.started = true;
    this.store.bindOwner(this.authService.parsedToken?.userId ?? null);
    this.getStateOverlay().start();
    this.initializeBrowserEntry();
    setStateHistoryGuard(this.handleBrowserPopState);
    if (this.router.navigated) {
      const currentUrl: string = this.resolveCurrentUrl();
      this.store.record(currentUrl);
      this.settledNavigationExists = true;
    }
    this.subscribeToRouter();
    this.subscribeToAuthOwner();
    this.hardwareBackSubscription = this.platform.backButton.subscribeWithPriority(10, (): void => {
      void this.back();
    });
    this.destroyRef.onDestroy((): void => {
      this.hardwareBackSubscription?.unsubscribe();
      this.hardwareBackSubscription = null;
      setStateHistoryGuard(null);
      this.completeBrowserTraversalOperations();
      this.rejectedForwardNavigationIds.clear();
    });
  }

  public async navigateForward(
    url: string | UrlTree | unknown[],
    options: StateNavigationOptions = {},
  ): Promise<boolean> {
    if (!await this.prepareImperativeNavigation()) return false;
    const targetUrl: string | null = this.resolveNavigationTargetUrl(url, options);
    const previousUrl: string | null = targetUrl
      ? this.store.resolvePreviousRouteUrl(this.resolveCurrentUrl(), targetUrl)
      : null;
    if (previousUrl) {
      return this.navigateBackTo(previousUrl, options);
    }
    return await this.navController.navigateForward(url, options);
  }

  public async navigateByUrl(
    url: string | UrlTree,
    options: StateNavigationOptions = {},
  ): Promise<boolean> {
    if (!await this.prepareImperativeNavigation()) return false;
    return await this.router.navigateByUrl(url, options);
  }

  public async navigateToSection(
    url: string,
    options: StateNavigationOptions = {},
  ): Promise<boolean> {
    if (this.sectionNavigationOperation) {
      await this.sectionNavigationOperation;
      return await this.navigateToSection(url, options);
    }

    const operation: Promise<boolean> = this.performSectionNavigation(url, options);
    this.sectionNavigationOperation = operation;
    try {
      return await operation;
    } finally {
      if (this.sectionNavigationOperation === operation) {
        this.sectionNavigationOperation = null;
      }
    }
  }

  public async back(
    fallbackUrl: string = STATE_ROUTE_ROOT,
    options: StateNavigationOptions = {},
  ): Promise<boolean> {
    if (this.backOperation) return await this.backOperation;

    const operation: Promise<boolean> = this.performBack(fallbackUrl, options);
    this.backOperation = operation;
    try {
      return await operation;
    } finally {
      if (this.backOperation === operation) {
        this.backOperation = null;
      }
    }
  }

  public replaceCurrentRoute(url: string): boolean {
    if (this.router.getCurrentNavigation()) return false;

    const normalizedUrl: string = normalizeStateRouteUrl(url);
    if (!this.store.replaceCurrent(normalizedUrl)) return false;

    const currentEntry: StateRouteBrowserEntry = this.currentBrowserEntry
      ?? this.createBrowserEntry(normalizedUrl, null);
    const nextEntry: StateRouteBrowserEntry = {
      ...currentEntry,
      url: normalizedUrl,
    };
    this.currentBrowserEntry = nextEntry;
    this.location.replaceState(normalizedUrl, undefined, this.buildBrowserState(nextEntry));
    return true;
  }

  private async performSectionNavigation(
    url: string,
    options: StateNavigationOptions,
  ): Promise<boolean> {
    if (!await this.prepareImperativeNavigation()) return false;

    const targetUrl: string = normalizeStateRouteUrl(url);
    if (this.isCanonicalSectionState(targetUrl)) return false;

    const currentPath: string = normalizeStateRoutePath(this.resolveCurrentUrl());
    if (currentPath !== STATE_ROUTE_ROOT) {
      const rooted: boolean = await this.navController.navigateBack(STATE_ROUTE_ROOT, {
        ...options,
        animated: false,
        replaceUrl: true,
      });
      if (!rooted && normalizeStateRoutePath(this.resolveCurrentUrl()) !== STATE_ROUTE_ROOT) {
        return false;
      }
    }
    if (targetUrl === STATE_ROUTE_ROOT) return true;

    return await this.navController.navigateForward(targetUrl, options);
  }

  private isCanonicalSectionState(targetUrl: string): boolean {
    if (normalizeStateRouteUrl(this.resolveCurrentUrl()) !== targetUrl) return false;

    const routes: readonly StateRouteEntry[] = this.store.routes();
    const expectedRouteCount: number = targetUrl === STATE_ROUTE_ROOT ? 1 : 2;
    if (
      routes.length !== expectedRouteCount
      || routes[0]?.path !== STATE_ROUTE_ROOT
      || routes[routes.length - 1]?.path !== normalizeStateRoutePath(targetUrl)
    ) {
      return false;
    }

    return true;
  }

  private async performBack(
    fallbackUrl: string,
    options: StateNavigationOptions,
  ): Promise<boolean> {
    if (await this.getStateOverlay().closeTop()) return true;

    this.syncCurrentBrowserEntryFromHistory();
    const currentUrl: string = this.resolveCurrentUrl();
    const targetUrl: string = this.resolveBackTargetUrl(currentUrl, fallbackUrl);
    return this.navigateBackTo(targetUrl, options);
  }

  private async navigateBackTo(targetUrl: string, options: StateNavigationOptions): Promise<boolean> {
    const currentUrl: string = this.resolveCurrentUrl();
    if (targetUrl === currentUrl) return false;
    const browserBackDelta: number | null = this.resolveBrowserBackDelta(targetUrl);
    if (browserBackDelta !== null && typeof history !== 'undefined') {
      this.navController.setDirection('back', options.animated);
      const completed: boolean = await this.waitForBrowserTraversal(
        browserBackDelta,
        targetUrl,
        (): boolean => this.isRouteSettledAt(targetUrl),
      );
      if (completed) return true;

      this.syncCurrentBrowserEntryFromHistory();
      console.warn('[StateRouter] browser Back did not reach the expected route', {targetUrl});
    }
    return await this.navController.navigateBack(targetUrl, {
      ...options,
      replaceUrl: true,
    });
  }

  private resolveNavigationTargetUrl(
    url: string | UrlTree | unknown[],
    options: StateNavigationOptions,
  ): string | null {
    try {
      if (typeof url === 'string') {
        return normalizeStateRouteUrl(url);
      }
      const urlTree: UrlTree = url instanceof UrlTree
        ? url
        : this.router.createUrlTree(url, options);
      return normalizeStateRouteUrl(this.router.serializeUrl(urlTree));
    } catch {
      return null;
    }
  }

  private subscribeToRouter(): void {
    this.router.events.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((event: RouterEvent): void => {
      if (event instanceof NavigationStart) {
        const acceptedEntry: StateRouteBrowserEntry | null = this.currentBrowserEntry;
        const targetEntry: StateRouteBrowserEntry | null = this.resolvePopStateBrowserEntry(event);
        const forwardEntry: StateRouteBrowserEntry | null = this.resolveForwardBrowserEntry(event);
        if (forwardEntry) {
          this.pendingNavigation = null;
          this.rejectedForwardNavigationIds.add(event.id);
          if (acceptedEntry) {
            void this.rejectForwardNavigation(acceptedEntry, event.id);
          }
          return;
        }

        const browserBack: boolean = event.navigationTrigger === 'popstate'
          && !!acceptedEntry
          && !!targetEntry
          && targetEntry.position < acceptedEntry.position;
        if (browserBack && this.programmaticBrowserTraversalCount === 0) {
          this.navController.setDirection('back', true);
        }

        this.syncCurrentBrowserEntryFromHistory();
        const currentNavigation = this.router.getCurrentNavigation();
        this.pendingNavigation = {
          replaceUrl: currentNavigation?.extras.replaceUrl === true,
          trigger: event.navigationTrigger,
        };
        return;
      }
      if (event instanceof NavigationEnd) {
        if (this.rejectedForwardNavigationIds.delete(event.id)) return;

        this.completeNavigation(event.urlAfterRedirects);
        return;
      }
      if (event instanceof NavigationSkipped) {
        if (this.rejectedForwardNavigationIds.delete(event.id)) return;

        this.pendingNavigation = null;
        this.syncCurrentBrowserEntryFromHistory();
        return;
      }
      if (
        event instanceof NavigationCancel
        || event instanceof NavigationError
      ) {
        if (this.rejectedForwardNavigationIds.delete(event.id)) return;

        this.pendingNavigation = null;
      }
    });
  }

  private resolveForwardBrowserEntry(event: NavigationStart): StateRouteBrowserEntry | null {
    if (!this.currentBrowserEntry) return null;

    const targetEntry: StateRouteBrowserEntry | null = this.resolvePopStateBrowserEntry(event);
    if (!targetEntry || targetEntry.position <= this.currentBrowserEntry.position) return null;

    return targetEntry;
  }

  private resolvePopStateBrowserEntry(event: NavigationStart): StateRouteBrowserEntry | null {
    if (event.navigationTrigger !== 'popstate') return null;

    const browserState: unknown = event.restoredState
      ?? (typeof history === 'undefined' ? null : history.state);
    return this.readBrowserEntry(browserState, event.url);
  }

  private async rejectForwardNavigation(
    acceptedEntry: StateRouteBrowserEntry,
    navigationId: number,
  ): Promise<void> {
    try {
      await this.rejectForwardTraversal(acceptedEntry, true);
    } finally {
      this.rejectedForwardNavigationIds.delete(navigationId);
    }
  }

  private async rejectForwardTraversal(
    acceptedEntry: StateRouteBrowserEntry,
    awaitRouter: boolean,
  ): Promise<void> {
    if (this.forwardRejectionOperation) {
      await this.forwardRejectionOperation;
      return;
    }

    const operation: Promise<void> = this.performForwardRejection(acceptedEntry, awaitRouter);
    this.forwardRejectionOperation = operation;
    try {
      await operation;
    } finally {
      if (this.forwardRejectionOperation === operation) {
        this.forwardRejectionOperation = null;
      }
    }
  }

  private async performForwardRejection(
    acceptedEntry: StateRouteBrowserEntry,
    awaitRouter: boolean,
  ): Promise<void> {
    const isAcceptedEntry = (): boolean => this.isCurrentBrowserEntry(acceptedEntry);
    const restored: boolean = await (awaitRouter
      ? this.waitForBrowserTraversal(-1, acceptedEntry.url, isAcceptedEntry)
      : this.waitForNativeTraversal(-1, isAcceptedEntry));
    if (restored) return;

    console.warn('[StateRouter] failed to reject browser Forward; restoring the accepted route', {
      url: acceptedEntry.url,
    });
    this.currentBrowserEntry = acceptedEntry;
    this.writeCurrentBrowserEntry();
    await this.navController.navigateBack(acceptedEntry.url, {
      animated: false,
      replaceUrl: true,
    });
  }

  private async recoverUnexpectedBrowserBack(
    expectedBackUrl: string,
    acceptedEntry: StateRouteBrowserEntry,
    targetPosition: number = Math.max(0, acceptedEntry.position - 1),
  ): Promise<void> {
    if (this.unexpectedBackOperation) {
      await this.unexpectedBackOperation;
      return;
    }

    const operation: Promise<void> = this.performUnexpectedBrowserBack(
      expectedBackUrl,
      acceptedEntry,
      targetPosition,
    );
    this.unexpectedBackOperation = operation;
    try {
      await operation;
    } finally {
      if (this.unexpectedBackOperation === operation) {
        this.unexpectedBackOperation = null;
      }
    }
  }

  private async performUnexpectedBrowserBack(
    expectedBackUrl: string,
    acceptedEntry: StateRouteBrowserEntry,
    targetPosition: number,
  ): Promise<void> {
    const recoveredEntry: StateRouteBrowserEntry = this.createBrowserEntry(
      acceptedEntry.url,
      null,
      targetPosition,
    );
    this.currentBrowserEntry = recoveredEntry;
    this.location.replaceState(
      acceptedEntry.url,
      undefined,
      this.buildBrowserState(recoveredEntry),
    );

    if (normalizeStateRouteUrl(expectedBackUrl) === normalizeStateRouteUrl(acceptedEntry.url)) {
      return;
    }

    this.navController.setDirection('back', true);
    await this.navController.navigateBack(expectedBackUrl, {
      animated: true,
      replaceUrl: true,
    });
    if (!this.isRouteSettledAt(expectedBackUrl)) {
      console.warn('[StateRouter] browser Back did not reach the logical predecessor', {expectedBackUrl});
      return;
    }
  }

  private waitForNativeTraversal(
    delta: number,
    isExpectedState: () => boolean,
  ): Promise<boolean> {
    if (typeof history === 'undefined' || this.pendingNativeTraversal) return Promise.resolve(false);

    return new Promise<boolean>((resolve): void => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let completed: boolean = false;
      const abort = (): void => complete(false);
      const complete = (result: boolean): void => {
        if (completed) return;

        completed = true;
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (this.pendingNativeTraversal?.complete === complete) {
          this.pendingNativeTraversal = null;
        }
        this.browserTraversalOperations.delete(abort);
        resolve(result);
      };
      this.pendingNativeTraversal = {complete, isExpectedState};
      this.browserTraversalOperations.add(abort);
      timeoutId = setTimeout(abort, StateRouterService.HISTORY_TRAVERSAL_TIMEOUT_MS);
      try {
        history.go(delta);
      } catch {
        abort();
      }
    });
  }

  private waitForBrowserTraversal(
    delta: number,
    expectedUrl: string,
    isExpectedState: () => boolean,
  ): Promise<boolean> {
    if (typeof history === 'undefined' || this.pendingBrowserTraversal) return Promise.resolve(false);

    this.programmaticBrowserTraversalCount += 1;
    return new Promise<boolean>((resolve): void => {
      let navigationId: number | null = null;
      let routerSubscription: Subscription | null = null;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let completed: boolean = false;
      const complete = (result: boolean): void => {
        if (completed) return;

        completed = true;
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        routerSubscription?.unsubscribe();
        if (this.pendingBrowserTraversal?.complete === complete) {
          this.pendingBrowserTraversal = null;
        }
        this.browserTraversalOperations.delete(abort);
        this.programmaticBrowserTraversalCount = Math.max(0, this.programmaticBrowserTraversalCount - 1);
        resolve(result);
      };
      const abort = (): void => complete(false);
      this.pendingBrowserTraversal = {
        complete,
        expectedUrl: normalizeStateRouteUrl(expectedUrl),
        isExpectedState,
      };
      routerSubscription = this.router.events.subscribe((event: RouterEvent): void => {
        if (event instanceof NavigationStart) {
          if (navigationId === null && event.navigationTrigger === 'popstate') {
            navigationId = event.id;
          }
          return;
        }
        if (
          navigationId !== null
          && this.isTerminalRouterEvent(event)
          && event.id === navigationId
        ) {
          complete(isExpectedState());
        }
      });
      this.browserTraversalOperations.add(abort);
      timeoutId = setTimeout(abort, StateRouterService.HISTORY_TRAVERSAL_TIMEOUT_MS);
      try {
        history.go(delta);
      } catch {
        abort();
      }
    });
  }

  private completeBrowserTraversalOperations(): void {
    [...this.browserTraversalOperations].forEach((complete: () => void): void => complete());
  }

  private isCurrentBrowserEntry(expectedEntry: StateRouteBrowserEntry): boolean {
    if (typeof history === 'undefined' || !this.isRouteSettledAt(expectedEntry.url)) return false;

    const currentEntry: StateRouteBrowserEntry | null = this.readBrowserEntry(
      history.state,
      this.resolveCurrentUrl(),
    );
    return currentEntry?.sessionId === expectedEntry.sessionId
      && currentEntry.position === expectedEntry.position
      && normalizeStateRouteUrl(currentEntry.url) === normalizeStateRouteUrl(expectedEntry.url);
  }

  private isRouteSettledAt(expectedUrl: string): boolean {
    const normalizedExpectedUrl: string = normalizeStateRouteUrl(expectedUrl);
    return normalizeStateRouteUrl(this.router.url) === normalizedExpectedUrl
      && normalizeStateRouteUrl(this.resolveCurrentUrl()) === normalizedExpectedUrl;
  }

  private subscribeToAuthOwner(): void {
    this.authService.parsedToken$.pipe(
      map((token: ParsedToken | null): number | null => token?.userId ?? null),
      distinctUntilChanged(),
      skip(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((userId: number | null): void => {
      if (!this.store.bindOwner(userId)) return;

      this.startAuthOwnerReset();
    });
  }

  private startAuthOwnerReset(): void {
    const revision: number = ++this.authOwnerRevision;
    const reset = (): Promise<void> => this.performAuthOwnerReset(revision);
    const operation: Promise<void> = this.authOwnerResetOperation
      ? this.authOwnerResetOperation.then(reset, reset)
      : reset();
    this.authOwnerResetOperation = operation;
    const release = (): void => {
      if (this.authOwnerResetOperation === operation) {
        this.authOwnerResetOperation = null;
      }
    };
    void operation.then(release, release);
  }

  private async performAuthOwnerReset(revision: number): Promise<void> {
    await this.getStateOverlay().closeAll();
    if (revision !== this.authOwnerRevision) return;

    const currentUrl: string = this.resolveCurrentUrl();
    const currentPosition: number = this.currentBrowserEntry?.position ?? 0;
    this.currentBrowserEntry = this.createBrowserEntry(currentUrl, null, currentPosition);
    this.store.record(currentUrl);
    this.writeCurrentBrowserEntry();
  }

  private async prepareImperativeNavigation(): Promise<boolean> {
    await this.waitForNavigationStability();
    if (!await this.getStateOverlay().closeAll()) return false;

    await this.waitForNavigationStability();
    return true;
  }

  private async waitForNavigationStability(): Promise<void> {
    while (true) {
      const operations: readonly Promise<unknown>[] = [
        this.authOwnerResetOperation,
        this.backOperation,
        this.forwardRejectionOperation,
        this.unexpectedBackOperation,
      ].filter((operation: Promise<unknown> | null): operation is Promise<unknown> => operation !== null);
      if (!operations.length) return;

      await Promise.allSettled(operations);
    }
  }

  private completeNavigation(url: string): void {
    const normalizedUrl: string = normalizeStateRouteUrl(url);
    const pendingNavigation: PendingStateNavigation | null = this.pendingNavigation;
    this.pendingNavigation = null;

    if (!this.settledNavigationExists) {
      this.settledNavigationExists = true;
      const currentEntry: StateRouteBrowserEntry = this.currentBrowserEntry
        ?? this.createBrowserEntry(normalizedUrl, null);
      this.currentBrowserEntry = {
        ...currentEntry,
        url: normalizedUrl,
      };
    } else if (pendingNavigation?.trigger === 'popstate') {
      const browserState: unknown = typeof history === 'undefined' ? null : history.state;
      this.currentBrowserEntry = this.readBrowserEntry(browserState, normalizedUrl)
        ?? this.createBrowserEntry(normalizedUrl, null);
    } else {
      this.currentBrowserEntry = this.createNextBrowserEntry(
        normalizedUrl,
        pendingNavigation?.replaceUrl === true,
      );
    }

    if (normalizeStateRoutePath(normalizedUrl) === STATE_ROUTE_ROOT && this.currentBrowserEntry) {
      this.currentBrowserEntry = this.createBrowserEntry(
        normalizedUrl,
        null,
        this.currentBrowserEntry.position,
      );
    }

    this.writeCurrentBrowserEntry();
    this.store.record(normalizedUrl);
  }

  private initializeBrowserEntry(): void {
    const normalizedUrl: string = this.resolveCurrentUrl();
    const restoredEntry: StateRouteBrowserEntry | null = typeof history === 'undefined'
      ? null
      : this.readBrowserEntry(history.state, normalizedUrl);
    this.currentBrowserEntry = restoredEntry ?? this.createBrowserEntry(normalizedUrl, null);
    this.writeCurrentBrowserEntry();
  }

  private syncCurrentBrowserEntryFromHistory(): void {
    if (typeof history === 'undefined') return;

    const normalizedUrl: string = this.resolveCurrentUrl();
    const browserEntry: StateRouteBrowserEntry | null = this.readBrowserEntry(
      history.state,
      normalizedUrl,
    );
    if (browserEntry) {
      this.currentBrowserEntry = browserEntry;
    }
  }

  private createNextBrowserEntry(url: string, replaceUrl: boolean): StateRouteBrowserEntry {
    const currentEntry: StateRouteBrowserEntry = this.currentBrowserEntry
      ?? this.createBrowserEntry(url, null);
    if (replaceUrl) {
      return {
        ...currentEntry,
        url,
      };
    }
    return this.createBrowserEntry(url, currentEntry);
  }

  private createBrowserEntry(
    url: string,
    predecessor: StateRouteBrowserEntry | null,
    position: number = predecessor ? predecessor.position + 1 : 1,
  ): StateRouteBrowserEntry {
    const normalizedUrl: string = normalizeStateRouteUrl(url);
    return {
      position,
      predecessorPosition: predecessor?.position ?? null,
      predecessorUrl: predecessor?.url ?? null,
      schemaVersion: StateRouterService.BROWSER_STATE_SCHEMA_VERSION,
      sessionId: this.store.sessionId,
      url: normalizedUrl,
    };
  }

  private resolveBrowserBackDelta(targetUrl: string): number | null {
    const entry: StateRouteBrowserEntry | null = this.currentBrowserEntry;
    if (!entry || entry.sessionId !== this.store.sessionId) return null;
    if (
      entry.predecessorPosition === null
      || normalizeStateRouteUrl(entry.predecessorUrl ?? '') !== normalizeStateRouteUrl(targetUrl)
    ) {
      return null;
    }

    const delta: number = entry.predecessorPosition - entry.position;
    return delta < 0 ? delta : null;
  }

  private resolveBackTargetUrl(currentUrl: string, fallbackUrl: string): string {
    return this.resolveExplicitBackUrl(currentUrl)
      ?? normalizeStateRouteUrl(this.store.resolveBackUrl(currentUrl, fallbackUrl));
  }

  private resolveExplicitBackUrl(url: string): string | null {
    const queryParams: Record<string, unknown> = this.router.parseUrl(url).queryParams;
    const keys: readonly string[] = ['back', 'returnUrl', 'defaultHref'];
    for (const key of keys) {
      const value: unknown = queryParams[key];
      if (typeof value === 'string' && isInternalStateRouteUrl(value)) {
        return normalizeStateRouteUrl(value);
      }
    }
    return null;
  }

  private resolveCurrentUrl(): string {
    const url: string = typeof window === 'undefined'
      ? this.router.url
      : `${window.location.pathname}${window.location.search}${window.location.hash}`;
    return normalizeStateRouteUrl(url);
  }

  private getStateOverlay(): StateOverlayService {
    if (!this.stateOverlay) {
      this.stateOverlay = this.injector.get(StateOverlayService);
    }
    return this.stateOverlay;
  }

  private writeCurrentBrowserEntry(): void {
    if (!this.currentBrowserEntry || typeof history === 'undefined' || typeof window === 'undefined') return;
    history.replaceState(this.buildBrowserState(this.currentBrowserEntry), '', window.location.href);
  }

  private buildBrowserState(entry: StateRouteBrowserEntry): Record<string, unknown> {
    const browserState: unknown = typeof history === 'undefined' ? null : history.state;
    const state: Record<string, unknown> = this.isRecord(browserState) ? {...browserState} : {};
    state[StateRouterService.BROWSER_STATE_KEY] = entry;
    return state;
  }

  private readBrowserEntry(state: unknown, expectedUrl: string): StateRouteBrowserEntry | null {
    if (!this.isRecord(state)) return null;
    const value: unknown = state[StateRouterService.BROWSER_STATE_KEY];
    if (!this.isRecord(value)) return null;
    const position: unknown = value['position'];
    const predecessorPosition: unknown = value['predecessorPosition'];
    const predecessorUrl: unknown = value['predecessorUrl'];
    if (
      value['schemaVersion'] !== StateRouterService.BROWSER_STATE_SCHEMA_VERSION
      && value['schemaVersion'] !== StateRouterService.LEGACY_BROWSER_STATE_SCHEMA_VERSION
    ) return null;
    if (!this.isNonNegativeInteger(position)) return null;
    const normalizedPredecessorPosition: number | null = predecessorPosition === null
      ? null
      : this.isNonNegativeInteger(predecessorPosition) && predecessorPosition < position
        ? predecessorPosition
        : null;
    const normalizedPredecessorUrl: string | null = predecessorUrl === null
      ? null
      : typeof predecessorUrl === 'string' ? predecessorUrl : null;
    if (value['sessionId'] !== this.store.sessionId) return null;
    if (predecessorPosition !== null && normalizedPredecessorPosition === null) return null;
    if (predecessorUrl !== null && normalizedPredecessorUrl === null) return null;
    if ((normalizedPredecessorPosition === null) !== (normalizedPredecessorUrl === null)) return null;
    if (typeof value['url'] !== 'string' || !isInternalStateRouteUrl(value['url'])) return null;
    if (normalizedPredecessorUrl !== null && !isInternalStateRouteUrl(normalizedPredecessorUrl)) return null;
    if (normalizeStateRouteUrl(value['url']) !== normalizeStateRouteUrl(expectedUrl)) return null;
    return {
      position,
      predecessorPosition: normalizedPredecessorPosition,
      predecessorUrl: normalizedPredecessorUrl,
      schemaVersion: StateRouterService.BROWSER_STATE_SCHEMA_VERSION,
      sessionId: value['sessionId'],
      url: value['url'],
    };
  }

  private isNonNegativeInteger(value: unknown): value is number {
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  private isTerminalRouterEvent(
    event: RouterEvent,
  ): event is NavigationCancel | NavigationEnd | NavigationError | NavigationSkipped {
    return event instanceof NavigationCancel
      || event instanceof NavigationEnd
      || event instanceof NavigationError
      || event instanceof NavigationSkipped;
  }
}
